import * as React from 'react';
import { AlertMessage, AlertType, Form, SpinnerWrapper, HiddenInput } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { useParams, useHistory } from 'react-router-dom';
import { setMessage } from '../../../helpers/MessageHelper';
import { OrthodonticReferralApi } from '../../../api/OrthodonticReferralApi';
import { OrthodonticReferralSections } from './OrthodonticReferralSections/OrthodonticReferralSections';
import { IOrthodonticReferral, IOrthodonticReferralCreate } from '../../../models/IOrthodonticReferral';
import { IPatient } from '../../../models/IPatient';
import { OrthodonticReferralFooterButtons } from './OrthodonticReferralButtons/OrthodonticReferralFooterButtons';
import { UserContext, isInRole } from '../../../contexts/UserContext';
import { ALLMÄNTANDVÅRDBAS, URVALSTANDLÄKAREBAS, TANDREGLERINGBAS } from '../../../utils/RoleConstants';
import { ICareProvider } from '../../../models/ICareProvider';
import { TREATMENT1_TYPEID, TREATMENT2_TYPEID } from "../../../utils/OrthodonticReferralConstants";
import { OrthodonticReferralDropdownButtons } from './OrthodonticReferralButtons/OrthodonticReferralDropdownButtons';
import { EventLog } from "../../../components/EventLog/EventLog";

interface IOrthodonticReferralView {
    patient: IPatient;
}

export const OrthodonticReferral: React.FunctionComponent<IOrthodonticReferralView> = ({ patient }) => {
    const auth = useAuth();
    const api = new OrthodonticReferralApi(auth);
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [isFetchingOrthodonticReferral, setIsFetchingOrthodonticReferral] = React.useState<boolean>(false);
    const userInfo = React.useContext(UserContext);
    const hasRoleAllmantandvard = isInRole(ALLMÄNTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleOrtodontisttandvård = isInRole(TANDREGLERINGBAS, userInfo.roles ? userInfo.roles : []);
    const [orthodonticReferral, setOrthodonticReferral] = React.useState<IOrthodonticReferral>();

    React.useEffect(() => {
        if (id) {
            getOrthodonticReferral(parseInt(id), false);
        }
        else {
            setIsFetchingOrthodonticReferral(true);

            var referralCreateParams: IOrthodonticReferralCreate = { orthodonticReferralTypeId: TREATMENT1_TYPEID };
            if (history.location.state !== undefined && history.location.state !== null) {
                referralCreateParams = history.location.state as IOrthodonticReferralCreate;
            }

            const canEdit = (referralCreateParams.orthodonticReferralTypeId == TREATMENT1_TYPEID && hasRoleAllmantandvard) || (referralCreateParams.orthodonticReferralTypeId == TREATMENT2_TYPEID && hasRoleOrtodontisttandvård);
            
            var referrerCareProvider: ICareProvider,
                recipientCareProvider: ICareProvider;

            api.getOrthodonticReferralReferrer((careProvider) => {
                referrerCareProvider = careProvider;
            }, undefined, () => {
                api.getOrthodonticReferralRecipient(patient.patientId, (careProvider) => {
                    recipientCareProvider = careProvider;
                    var copy = history.location.state as IOrthodonticReferral;
                    if (copy?.isCopy) {
                        copy.consultantCareProvider = recipientCareProvider;
                        setOrthodonticReferral(copy)
                    } else {
                        setOrthodonticReferral({ canEdit: canEdit, consultantCareProvider: recipientCareProvider, referrerCareProvider: referrerCareProvider, orthodonticReferralTypeId: referralCreateParams.orthodonticReferralTypeId });
                    }
                }, undefined, () => setIsFetchingOrthodonticReferral(false));
            });
        }
    }, [userInfo, id])

    const getOrthodonticReferral = (orthodonticReferralId: number, refresh: boolean) => {
        setIsFetchingOrthodonticReferral(true);
        api.getOrthodonticReferral(orthodonticReferralId, refresh
            , (data) => {
                setOrthodonticReferral(data);
            }
            , (error) => changeErrorMessage(error)
            , () => setIsFetchingOrthodonticReferral(false))
    }

    const changeSuccessMessage = (message: string) => {
        const newMessage = setMessage(message, successMessage ?? "");
        setSuccessMessage(newMessage);
    }
    const changeErrorMessage = (message: string) => {
        const newMessage = setMessage(message, errorMessage ?? "");
        setErrorMessage(newMessage);
    }
    const copyModalSuccess = (copiedData: IOrthodonticReferral, successMessage: string) => {
        setOrthodonticReferral(copiedData);
        changeSuccessMessage(successMessage);
    }

    return (
        <SpinnerWrapper isLoading={isFetchingOrthodonticReferral}>
            <Form>
                <HiddenInput name="orthodonticReferralId" value={orthodonticReferral?.orthodonticReferralId?.toString() ?? "0"} />
                <section className="card">
                    <div className="card-header">
                        <div className="row">
                            <div className="col">
                                <h1>Ortodontiremiss {orthodonticReferral?.orthodonticReferralNumber && orthodonticReferral?.orthodonticReferralNumber !== "" ? "- " + orthodonticReferral?.orthodonticReferralNumber : ""}</h1>
                            </div>
                            {
                                orthodonticReferral?.unreadAnswer ?
                                    <div className="col">
                                        <h2 className="text-center">*** Oläst ***</h2>
                                    </div>
                                    : null
                            }
                            {
                                orthodonticReferral ?
                                    <div className="col text-right">
                                        <OrthodonticReferralDropdownButtons changeSuccessMessage={changeSuccessMessage} changeErrorMessage={changeErrorMessage} orthodonticReferralId={orthodonticReferral.orthodonticReferralId} getOrthodonticReferral={(id) => getOrthodonticReferral(id, true)} canRequestCompletion={orthodonticReferral.canRequestCompletion ?? false} canForward={orthodonticReferral.canForward ?? false} canShare={orthodonticReferral.canShare ?? false} hasDecisionInterceptive={orthodonticReferral.hasDecisionInterceptive ?? false} canExtendValidDate={orthodonticReferral.canExtendValidDate ?? false} decisionDate={orthodonticReferral.orthodonticReferralDecisionDate} />
                                    </div>
                                    : null
                            }
                        </div>
                    </div>
                    <div className="card-body">
                        <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                        <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                        {
                            orthodonticReferral ?
                                <>
                                    <OrthodonticReferralSections orthodonticReferral={orthodonticReferral} orthodonticReferralId={orthodonticReferral.orthodonticReferralId} patient={patient} isFetchingOrthodonticReferral={isFetchingOrthodonticReferral} changeSuccessMessage={changeSuccessMessage} changeErrorMessage={changeErrorMessage} getOrthodonticReferral={getOrthodonticReferral} hasRoleAllmantandvard={hasRoleAllmantandvard} hasRoleOrtodontisttandvård={hasRoleOrtodontisttandvård} />
                                    {
                                        orthodonticReferral?.events ?
                                            <>
                                                <EventLog id="referralEventLog" events={orthodonticReferral.events} />
                                            </>
                                            : null
                                    }
                                </>
                                : null
                        }
                    </div>
                    <div className="card-footer">
                        {
                            orthodonticReferral ?
                                <OrthodonticReferralFooterButtons
                                    orthodonticReferral={orthodonticReferral}
                                    patient={patient}
                                    changeSuccessMessage={changeSuccessMessage}
                                    changeErrorMessage={changeErrorMessage}
                                    getOrthodonticReferral={(id) => getOrthodonticReferral(id, true)}
                                    onCopySuccess={copyModalSuccess} />
                                : null
                        }
                    </div>
                </section>
            </Form>
        </SpinnerWrapper>
    )
}